import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Box, 
  Type, 
  Image as ImageIcon, 
  Layout, 
  Code, 
  Trash2, 
  Move, 
  Zap, 
  Copy, 
  Terminal, 
  Play, 
  Layers, 
  Grid, 
  Hand, 
  Eye, 
  RefreshCw, 
  Save, 
  MousePointer2, 
  ZoomIn, 
  ZoomOut, 
  RotateCcw, 
  Square, 
  Type as TypeIcon, 
  Check, 
  Clipboard, 
  ArrowUp, 
  ArrowDown, 
  Group, 
  Ungroup, 
  ArrowUpFromLine, 
  ArrowDownToLine, 
  Undo, 
  User, 
  Star, 
  TextCursor, 
  Circle,
  Minus,
  MessageSquare,
  Wand2,
  Moon,
  Sun
} from 'lucide-react';

/**
 * Velvisio - A Whiteboard AI Website Builder
 * Fixes: 
 * 1. Resolved ESLint errors (undef vars, unused vars, missing dependencies).
 * 2. Added automatic Tailwind CSS injection for the main app so it doesn't look "bare bones" locally.
 */

// --- Internal Shape Components ---

const ShapeTriangle = ({ size = 18, className, ...props }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} {...props}>
    <path d="M13.73 4a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3l-8-14z" />
  </svg>
);

const ShapeHeart = ({ size = 18, className, ...props }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} {...props}>
    <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
  </svg>
);

const ShapeHexagon = ({ size = 18, className, ...props }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} {...props}>
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
  </svg>
);

// --- Constants & Data ---

const UI_ELEMENTS = [
  { id: 'navbar', label: 'Navbar', icon: <Layout size={18} />, width: 800, height: 80 },
  { id: 'hero', label: 'Hero', icon: <Type size={18} />, width: 800, height: 400 },
  { id: 'features', label: 'Features', icon: <Box size={18} />, width: 800, height: 350 },
  { id: 'card', label: 'Card', icon: <Square size={18} />, width: 300, height: 400 },
  { id: 'image', label: 'Image', icon: <ImageIcon size={18} />, width: 300, height: 200 },
  { id: 'text', label: 'Text', icon: <TypeIcon size={18} />, width: 300, height: 100 },
  { id: 'button', label: 'Button', icon: <MousePointer2 size={18} />, width: 140, height: 50 },
  { id: 'input', label: 'Input', icon: <TextCursor size={18} />, width: 250, height: 50 },
  { id: 'avatar', label: 'Avatar', icon: <User size={18} />, width: 60, height: 60 },
  { id: 'icon', label: 'Icon', icon: <Star size={18} />, width: 50, height: 50 },
  { id: 'circle', label: 'Circle', icon: <Circle size={18} />, width: 100, height: 100 },
  { id: 'triangle', label: 'Triangle', icon: <ShapeTriangle size={18} />, width: 100, height: 100 },
  { id: 'heart', label: 'Heart', icon: <ShapeHeart size={18} />, width: 100, height: 100 },
  { id: 'hexagon', label: 'Hexagon', icon: <ShapeHexagon size={18} />, width: 100, height: 100 },
  { id: 'divider', label: 'Divider', icon: <Minus size={18} />, width: 800, height: 20 },
  { id: 'sidebar', label: 'Sidebar', icon: <Layers size={18} />, width: 250, height: 600 },
  { id: 'footer', label: 'Footer', icon: <Layout size={18} />, width: 800, height: 150 },
];

const MOCK_PREVIEWS = {
  navbar: (
    <div className="w-full h-full p-2 border-b bg-white flex justify-between items-center shadow-sm select-none overflow-hidden">
      <div className="font-bold text-indigo-600">Brand</div>
      <div className="flex gap-2 text-xs text-gray-400">
        <div className="h-2 w-8 bg-gray-200 rounded"></div>
        <div className="h-2 w-8 bg-gray-200 rounded"></div>
      </div>
    </div>
  ),
  hero: (
    <div className="w-full h-full bg-indigo-50 flex flex-col items-center justify-center text-center p-4 select-none overflow-hidden">
      <div className="h-4 w-1/2 bg-gray-300 rounded mb-2"></div>
      <div className="h-2 w-1/3 bg-gray-200 rounded mb-4"></div>
      <div className="h-6 w-24 bg-indigo-500 rounded"></div>
    </div>
  ),
  features: (
    <div className="w-full h-full bg-white border border-gray-100 p-2 grid grid-cols-3 gap-2 select-none overflow-hidden">
      {[1, 2, 3].map(i => (
        <div key={i} className="bg-gray-50 border rounded flex flex-col items-center justify-center">
          <div className="w-4 h-4 bg-blue-200 rounded-full mb-1"></div>
          <div className="h-1 w-8 bg-gray-300 rounded"></div>
        </div>
      ))}
    </div>
  ),
  card: (
    <div className="w-full h-full bg-white rounded-lg border shadow-sm flex flex-col overflow-hidden select-none">
      <div className="h-32 bg-gray-200 w-full flex items-center justify-center text-gray-400 text-xs shrink-0">Image Area</div>
      <div className="p-3 space-y-2">
        <div className="h-3 w-3/4 bg-gray-300 rounded"></div>
        <div className="h-2 w-full bg-gray-100 rounded"></div>
        <div className="h-6 w-full bg-indigo-600 rounded mt-2"></div>
      </div>
    </div>
  ),
  image: (
    <div className="w-full h-full bg-gray-100 border border-gray-300 flex items-center justify-center text-gray-400 select-none overflow-hidden relative">
      <ImageIcon size={32} className="opacity-20" />
      <div className="absolute inset-0 bg-gradient-to-tr from-transparent to-gray-200/50"></div>
    </div>
  ),
  text: (
    <div className="w-full h-full bg-transparent border border-dashed border-gray-300 p-2 flex flex-col gap-2 select-none overflow-hidden justify-center">
        <div className="h-2 w-full bg-gray-200 rounded"></div>
        <div className="h-2 w-2/3 bg-gray-200 rounded"></div>
    </div>
  ),
  button: (
    <div className="w-full h-full bg-indigo-600 rounded-md flex items-center justify-center text-white text-xs font-bold shadow-sm select-none overflow-hidden">
      Button
    </div>
  ),
  input: (
    <div className="w-full h-full bg-white border border-gray-300 rounded flex items-center px-3 text-xs text-gray-400 shadow-sm select-none">
      Input field...
    </div>
  ),
  avatar: (
    <div className="w-full h-full bg-gray-200 rounded-full flex items-center justify-center border-2 border-white shadow-sm select-none overflow-hidden">
      <User size={24} className="text-gray-400" />
    </div>
  ),
  icon: (
    <div className="w-full h-full flex items-center justify-center text-indigo-600 select-none">
       <Star size="80%" fill="currentColor" className="opacity-20" />
    </div>
  ),
  circle: (
    <div className="w-full h-full bg-indigo-100 rounded-full border border-indigo-200 flex items-center justify-center shadow-sm select-none">
      <div className="text-[10px] text-indigo-400">Circle</div>
    </div>
  ),
  triangle: (
    <div className="w-full h-full flex items-center justify-center select-none">
      <div className="w-0 h-0 border-l-[25px] border-l-transparent border-r-[25px] border-r-transparent border-b-[50px] border-b-indigo-200"></div>
    </div>
  ),
  heart: (
    <div className="w-full h-full flex items-center justify-center text-pink-300 select-none">
       <ShapeHeart size="80%" fill="currentColor" />
    </div>
  ),
  hexagon: (
    <div className="w-full h-full flex items-center justify-center text-emerald-300 select-none">
       <ShapeHexagon size="80%" fill="currentColor" />
    </div>
  ),
  divider: (
    <div className="w-full h-full flex items-center justify-center select-none">
      <div className="w-full h-px bg-gray-300"></div>
    </div>
  ),
  sidebar: (
    <div className="w-full h-full bg-gray-50 border-r p-2 flex flex-col gap-2 select-none overflow-hidden">
      <div className="h-4 w-16 bg-gray-300 rounded mb-2"></div>
      {[1, 2, 3, 4, 5].map(i => (
        <div key={i} className="h-6 w-full bg-white border rounded"></div>
      ))}
    </div>
  ),
  footer: (
    <div className="w-full h-full bg-gray-800 flex items-center justify-center select-none overflow-hidden">
      <div className="h-2 w-24 bg-gray-600 rounded"></div>
    </div>
  )
};

// --- API Helpers ---

const generateCodeWithGemini = async (items, existingCode = null, modifications = null, context = "", customPrompt = "") => {

  const model = "gemini-3-flash-preview";
    
  if (!items || items.length === 0) return "// Drop some items on the whiteboard to start coding!";

  let userPrompt = "";
  let systemPrompt = "";

  // Determine Mode: Refinement (Save/Edit) vs Generation (New)
  const isRefining = existingCode && (customPrompt || (modifications && Object.keys(modifications).length > 0));

  if (isRefining) {
      // --- MODE 1: REFINEMENT / SAVING EDITS ---
      // We use a strictly different prompt to prevent hallucinating a new website.
      
      let modsList = "No manual layout modifications.";
      if (modifications && Object.entries(modifications).length > 0) {
          modsList = Object.entries(modifications).map(([id, mods]) => {
              let str = `Element ID '${id}':`;
              let changes = [];
              if (mods.width || mods.height) changes.push(`UPDATE CLASS to w-[${mods.width}px] h-[${mods.height}px]`);
              if (mods.text) changes.push(`CHANGE TEXT content to: "${mods.text}"`);
              if (mods.color) changes.push(`CHANGE COLOR (text or bg) to: "${mods.color}"`);
              if (mods.fontSize) changes.push(`CHANGE FONT SIZE to: "${mods.fontSize}px"`);
              if (mods.zIndex) changes.push(`CHANGE Z-INDEX to: "${mods.zIndex}"`);
              
              // CRITICAL: Handle Position Changes (dx, dy)
              if (mods.dx || mods.dy) {
                 changes.push(`ADD/UPDATE STYLE prop with transform: translate(${mods.dx||0}px, ${mods.dy||0}px)`);
              }

              if (changes.length === 0) return null;
              return str + " " + changes.join(", ") + ".";
          }).filter(Boolean).join('\n');
      }

      systemPrompt = `
        You are an expert React Refactoring Agent.
        Your ONLY job is to apply a specific list of updates to the provided React code.
        
        CRITICAL RULES:
        1. **PRESERVE STRUCTURE**: Do NOT regenerate the layout from scratch. Keep the existing component structure, imports, and logic exactly as they are unless specifically asked to change them.
        2. **APPLY MODIFICATIONS**: Strictly apply the width, height, text, color, and font-size changes listed in the prompt to the matching elements (matched by \`data-vibe-id\`).
        3. **POSITIONING (TRANSFORMS)**: If a modification request includes "transform: translate(...)", you MUST add or update the \`style\` prop of that specific element.
           - Example: \`<div ... style={{ transform: 'translate(20px, 40px)' }} ...>\`
           - Do NOT try to convert these arbitrary pixel translations into Tailwind classes. Use the \`style\` prop for translations.
        4. **OUTPUT FULL CODE**: Return the completely updated, runnable React component. Do not summarize.
        
        Note on IDs: The code uses \`data-vibe-id\` attributes. Use these to locate the elements to update.
      `;

      userPrompt = `
        EXISTING CODE:
        ${existingCode}

        REQUESTED MODIFICATIONS LIST:
        ${modsList}

        ADDITIONAL INSTRUCTIONS:
        ${customPrompt || "Apply the modifications above to the code. Ensure all manual resizing, text edits, and POSITION/TRANSFORM changes are reflected in the code."}
      `;

  } else {
      // --- MODE 2: GENERATION (FROM WHITEBOARD) ---
      
      const sortedItems = [...items].sort((a, b) => a.y - b.y || a.x - b.x);
      const minX = Math.min(...items.map(i => i.x));
      const maxX = Math.max(...items.map(i => i.x + i.width));
      const totalWidth = maxX - minX;
      
      const itemsWithContext = sortedItems.map(item => {
          const potentialParents = sortedItems.filter(p => 
              p.uniqueId !== item.uniqueId && 
              (p.zIndex || 10) < (item.zIndex || 10) &&
              item.x >= p.x && 
              item.y >= p.y && 
              (item.x + item.width) <= (p.x + p.width) && 
              (item.y + item.height) <= (p.y + p.height)
          );
          
          potentialParents.sort((a, b) => (b.zIndex || 10) - (a.zIndex || 10));
          const parent = potentialParents[0];
          
          return { ...item, parentId: parent ? parent.uniqueId : null };
      });

      const layoutDescription = itemsWithContext.map(i => {
        const el = UI_ELEMENTS.find(e => e.id === i.type);
        const relativeX = Math.round(i.x - minX);
        const parentInfo = i.parentId ? ` (NESTED INSIDE: ${i.parentId})` : " (ROOT LEVEL)";
        return `- ${el.label} (ID: ${i.uniqueId})${parentInfo} DIMENSIONS: {w:${Math.round(i.width)}px, h:${Math.round(i.height)}px} POS: {x:${relativeX}px, y:${Math.round(i.y)}px}`;
      }).join('\n');

      systemPrompt = `
        You are a World-Class UI Engineer & Designer.
        Your goal: Translate the provided JSON layout into React/Tailwind code with perfect fidelity to the requested dimensions.

        CRITICAL RULE #1: **EXACT OUTER DIMENSIONS**
        - You MUST use the exact width and height from the JSON for the **outermost containers** of each element using \`w-[...px] h-[...px]\`.
        - Do NOT use \`min-h-screen\` or full screen layouts unless the element itself is huge.

        CRITICAL RULE #2: **FLUID INTERNAL CONTENT & PADDING**
        - The content INSIDE these fixed containers must adapt beautifully.
        - **PADDING MANDATE**: ALL Containers (Cards, Hero, Features, Sections) MUST have ample padding (e.g., \`p-6\` or \`p-8\`) to ensure text NEVER touches the edges.
        - **FOR CARDS/CONTAINERS**: Use \`flex flex-col h-full\`.
          1. **Images/Headers**: Use \`shrink-0\` so they don't get squashed.
          2. **Text/Body**: 
             - Use \`flex-1 min-h-0 overflow-hidden relative\`.
             - **SPACING MANDATE**: Use \`gap-4\` or \`space-y-4\` in the flex container to strictly separate text from buttons/footers.
             - **TRUNCATION MANDATE**: If text is long, you MUST use \`line-clamp-3\` or \`line-clamp-4\` to prevent it from flowing UNDER the button or footer.
          3. **Buttons/Footers**: Use \`shrink-0 mt-auto\` so they stay anchored at the bottom.

        CRITICAL RULE #3: **HIGH-END AESTHETICS**
        - Shadows: \`shadow-[0_8px_30px_rgb(0,0,0,0.12)]\`.
        - Glassmorphism: \`backdrop-blur-xl bg-white/70 border border-white/20\`.
        - Gradients: \`bg-gradient-to-br from-indigo-50 to-blue-50\`.
        
        CRITICAL RULE #4: **ZERO EXTERNAL DEPENDENCIES**
        - NO IMPORTS (No Framer Motion, No Lucide). Use inline SVGs.
        - Pure React/Tailwind.

        ${context ? `\nCONTEXT/THEME: The user wants a website about: "${context}". customizing text, images, and colors to match this theme.` : ''}
          
        Technical Output:
        - Output ONLY the functional React component named 'GeneratedPage'.
        - Keep \`data-vibe-id\` attributes on the elements corresponding to the JSON IDs.
      `;

      userPrompt = `
        Whiteboard Context:
        - Total Design Width: ${Math.round(totalWidth)}px
        - Element List (Sorted by Y position):
        ${layoutDescription}
        
        ${context ? `Theme: ${context}` : ''}
        
        Generate the React component. 
        - Map each item to a React element.
        - **IMPORTANT:** If items are nested (have a parentId), they MUST be inside that parent's div.
        - **LAYOUT STRATEGY:** - For "Cards" or "Heroes" defined with specific H/W: Use \`w-[..px] h-[..px] flex flex-col overflow-hidden\`.
           - **SPACING:** Use \`p-6\` padding on containers.
           - **TEXT:** Put text inside a \`flex-1 min-h-0 overflow-hidden relative\` wrapper with \`line-clamp-3\`.
           - **ACTIONS:** Put buttons/actions in a \`shrink-0 mt-4\` wrapper at the bottom.
      `;
  }

  // API Call with Exponential Backoff
  const callBackend = async (attempt = 0) => {
    try {
      const response = await fetch('/api/generate', {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          userPrompt, 
          systemPrompt,
          model 
        }),
      });

      if (!response.ok) {
         if (response.status === 429 && attempt < 5) {
             const delay = Math.pow(2, attempt) * 1000;
             await new Promise(resolve => setTimeout(resolve, delay));
             return callBackend(attempt + 1);
         }
         throw new Error(`Server Error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
       if (attempt < 5) {
           const delay = Math.pow(2, attempt) * 1000;
           await new Promise(resolve => setTimeout(resolve, delay));
           return callBackend(attempt + 1);
       }
       throw error;
    }
  };

  try {
    const data = await callBackend();
    let text = data.candidates?.[0]?.content?.parts?.[0]?.text || "// Error generating code.";
    text = text.replace(/```(jsx|javascript|js|react)?/g, '').replace(/```/g, '').trim();
    return text;
  } catch (error) {
    console.error(error);
    return `// Error: ${error.message}. Please check your API Key on Vercel.`;
  }
};

// --- Components ---

const LivePreview = ({ code, isEditMode, onLayoutUpdate, zoom, initialModifications, selectedColor, selectedFontSize, onSelectionChange, onContextMenu, isDarkMode }) => {
  const iframeRef = useRef(null);

  useEffect(() => {
    const handleMessage = (e) => {
      if (e.data.type === 'LAYOUT_UPDATE') onLayoutUpdate(e.data);
      if (e.data.type === 'SELECTION_CHANGE') onSelectionChange(e.data.id);
      if (e.data.type === 'CLOSE_CONTEXT_MENU') onContextMenu(null);
      if (e.data.type === 'CONTEXT_MENU') {
          // Adjust coordinates based on iframe position and zoom
          if (iframeRef.current) {
              const rect = iframeRef.current.getBoundingClientRect();
              onContextMenu({
                  x: rect.left + (e.data.x * zoom),
                  y: rect.top + (e.data.y * zoom),
                  isBackground: e.data.isBackground
              });
          }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onLayoutUpdate, onSelectionChange, onContextMenu, zoom]);

  useEffect(() => {
    if (!iframeRef.current?.contentWindow) return;
    iframeRef.current.contentWindow.postMessage({ type: 'MODE_CHANGE', isEditMode }, '*');
    iframeRef.current.contentWindow.postMessage({ type: 'ZOOM_CHANGE', zoom }, '*');
    if (initialModifications) {
       iframeRef.current.contentWindow.postMessage({ type: 'SYNC_MODIFICATIONS', modifications: initialModifications }, '*');
    }
  }, [isEditMode, zoom, initialModifications]);

  useEffect(() => {
    if (!iframeRef.current?.contentWindow || !selectedColor) return;
    iframeRef.current.contentWindow.postMessage({ type: 'APPLY_COLOR', color: selectedColor }, '*');
  }, [selectedColor]);

  useEffect(() => {
    if (!iframeRef.current?.contentWindow || !selectedFontSize) return;
    iframeRef.current.contentWindow.postMessage({ type: 'APPLY_FONT_SIZE', fontSize: selectedFontSize }, '*');
  }, [selectedFontSize]);

  useEffect(() => {
    if (!iframeRef.current || !code) return;
    const sanitizedCode = code.replace(/import\s+.*?from\s+['"].*?['"];?/g, '').replace(/export\s+default\s+/g, '').replace(/export\s+/g, '');
    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <script src="https://cdn.tailwindcss.com"></script>
          <script src="https://unpkg.com/react@18/umd/react.development.js"></script>
          <script src="https://unpkg.com/react-dom@18/umd/react-dom.development.js"></script>
          <script src="https://unpkg.com/@babel/standalone/babel.min.js"></script>
          <style>
            body { margin: 0; padding: 0; background-color: ${isDarkMode ? '#111827' : '#f8fafc'}; color: ${isDarkMode ? '#f3f4f6' : '#1f2937'}; user-select: none; -webkit-user-select: none; overflow-x: hidden; }
            .vibe-hover:hover { outline: 1px dashed #6366f1; cursor: pointer; }
            /* Use boxShadow for selection to avoid layout shifts and ensure visibility */
            .vibe-selected { box-shadow: 0 0 0 2px #6366f1, 0 0 0 4px rgba(99, 102, 241, 0.2); position: relative; z-index: 50; }
            .resize-handle { position: absolute; bottom: -6px; right: -6px; width: 12px; height: 12px; background: white; border: 2px solid #6366f1; border-radius: 50%; cursor: se-resize; z-index: 60; display: none; }
            .vibe-selected > .resize-handle { display: block; }
            [contenteditable="true"] { outline: 2px dashed #10b981; cursor: text; min-width: 10px; user-select: text !important; -webkit-user-select: text !important; overflow-wrap: break-word; word-break: break-word; white-space: normal; }
            * { box-sizing: border-box; overflow-wrap: break-word; }
            /* Global Image Resizing to prevent blowouts */
            img, video { max-width: 100%; height: auto; object-fit: cover; }
            /* Hide scrollbars for cleaner preview */
            ::-webkit-scrollbar { width: 0px; background: transparent; }
          </style>
        </head>
        <body>
          <div id="root"></div>
          <script>
              window.onerror = function(message, source, lineno, colno, error) {
                  const root = document.getElementById('root');
                  root.innerHTML = '<div style="color:red; padding:20px; background:#fee; border:1px solid red; margin:20px; border-radius:8px;"><strong>Runtime Error:</strong> ' + message + '</div>';
                  return true;
              };

              window.IS_EDIT_MODE = ${isEditMode};
              window.CURRENT_ZOOM = ${zoom};
              window.selectedIds = new Set();
              window.transformState = ${JSON.stringify(initialModifications || {})};
              window.previousModifiedIds = new Set();
              window.lastAppliedState = {}; 

              // --- AUTO SCALER SCRIPT (ENHANCED) ---
              function checkTextOverflow() {
                  if(window.IS_EDIT_MODE) return; 
                  
                  const elements = document.querySelectorAll('[data-vibe-id]');
                  elements.forEach(el => {
                        // Fix: Support SVG elements which don't have innerText
                        const textContent = el.innerText || el.textContent || "";
                        if(el.children.length === 0 || (el.children.length > 0 && textContent.length > 0)) {
                           let fontSize = parseInt(window.getComputedStyle(el).fontSize);
                           if(!fontSize || fontSize < 6) return;
                           
                           // Only scale down if not manually modified
                           const isModified = window.transformState[el.getAttribute('data-vibe-id')]?.fontSize;
                           if (isModified) return;

                           while ((el.scrollHeight > el.clientHeight + 2 || el.scrollWidth > el.clientWidth + 2) && fontSize > 8) {
                               fontSize--;
                               el.style.fontSize = fontSize + 'px';
                               el.style.lineHeight = '1.1'; 
                           }
                        }
                  });
              }
              
              setInterval(checkTextOverflow, 1000);
              // --------------------------

              window.addEventListener('message', (e) => {
               if (e.data.type === 'MODE_CHANGE') {
                 window.IS_EDIT_MODE = e.data.isEditMode;
                 if(!window.IS_EDIT_MODE) clearSelection();
               }
               if (e.data.type === 'ZOOM_CHANGE') window.CURRENT_ZOOM = e.data.zoom;
               if (e.data.type === 'SYNC_MODIFICATIONS') { 
                  window.transformState = e.data.modifications; 
                  applyAllTransforms(); 
               }
               if (e.data.type === 'APPLY_COLOR') applyColorToSelection(e.data.color);
               if (e.data.type === 'APPLY_FONT_SIZE') applyFontSizeToSelection(e.data.fontSize);
              });

              window.addEventListener('click', (e) => {
                  window.parent.postMessage({ type: 'CLOSE_CONTEXT_MENU' }, '*');
              });
              
              function applyColorToSelection(color) {
                  if (!window.selectedIds.size) return;
                  window.selectedIds.forEach(id => {
                      const el = getElementByIdOrPath(id);
                      if (el) {
                          const state = getTransform(id);
                          state.color = color;
                          
                          const isContainer = el.tagName === 'DIV' || el.tagName === 'NAV' || el.tagName === 'SECTION'; 
                          if (isContainer && !el.innerText.trim()) el.style.backgroundColor = color; 
                          else el.style.color = color;
                          
                          window.parent.postMessage({ type: 'LAYOUT_UPDATE', id, color }, '*');
                      }
                  });
              }

              function applyFontSizeToSelection(fontSize) {
                  if (!window.selectedIds.size) return;
                  window.selectedIds.forEach(id => {
                      const el = getElementByIdOrPath(id);
                      if (el) {
                          const state = getTransform(id);
                          state.fontSize = fontSize;
                          el.style.fontSize = fontSize + 'px';
                          window.parent.postMessage({ type: 'LAYOUT_UPDATE', id, fontSize }, '*');
                      }
                  });
              }

              let isDragging = false, isResizing = false, isEditingText = false;
              let startX, startY, startWidth, startHeight, activeElement = null;
              let lockedRect = null;
              
              function getTransform(id) { if (!window.transformState[id]) window.transformState[id] = {}; return window.transformState[id]; }

              function getElementByIdOrPath(idOrPath) {
                  if (!idOrPath) return null;
                  if (idOrPath.includes('::')) {
                      const [parentId, childPath] = idOrPath.split('::');
                      const parent = document.querySelector(\`[data-vibe-id="\${parentId}"]\`);
                      if (!parent) return null;
                      const [tagName, index] = childPath.split('-');
                      const children = Array.from(parent.getElementsByTagName(tagName));
                      return children[parseInt(index)] || null;
                  }
                  return document.querySelector(\`[data-vibe-id="\${idOrPath}"]\`);
              }

              function applyAllTransforms() {
               const currentIds = new Set(Object.keys(window.transformState));
               const allIds = new Set([...window.previousModifiedIds, ...currentIds]);

               allIds.forEach(id => {
                 const el = getElementByIdOrPath(id);
                 if (el) {
                   const state = window.transformState[id];
                   const lastState = window.lastAppliedState[id] || {};
                   
                   if (!state) {
                       el.style.transform = '';
                       el.style.width = '';
                       el.style.height = '';
                       el.style.flex = '';
                       el.style.maxWidth = '';
                       el.style.minWidth = '';
                       el.style.color = '';
                       el.style.backgroundColor = '';
                       el.style.fontSize = '';
                       el.style.zIndex = '';
                       removeResizeHandle(el);
                   } else {
                       const { dx, dy, width, height, text, color, fontSize, zIndex } = state;
                       
                       // Transform
                       if (dx !== undefined || dy !== undefined) el.style.transform = \`translate(\${dx||0}px, \${dy||0}px)\`;
                       else el.style.transform = '';

                       // Dimensions & Flex 
                       if (width) el.style.width = width + 'px'; 
                       if (height) el.style.height = height + 'px';
                       
                       if (width || height) { 
                           // FORCE FLEX NONE TO ALLOW RESIZING OF NESTED ELEMENTS
                           el.style.flex = 'none'; 
                           el.style.maxWidth = 'none';
                           el.style.minWidth = '0'; // Allow shrinking
                       } else {
                           el.style.flex = ''; 
                           el.style.maxWidth = '';
                           el.style.minWidth = '';
                       }
                       
                       // Text
                       if (text !== undefined && el.innerText !== text) el.innerText = text;
                       
                       // Color
                       if (color) {
                          const isContainer = el.tagName === 'DIV' || el.tagName === 'NAV' || el.tagName === 'SECTION'; 
                          if (isContainer && !el.innerText.trim()) el.style.backgroundColor = color; 
                          else el.style.color = color;
                       } else {
                          el.style.color = '';
                          el.style.backgroundColor = '';
                       }

                       // Font Size
                       if (fontSize) el.style.fontSize = fontSize + 'px';

                       // Z-Index
                       if (zIndex !== undefined) el.style.zIndex = zIndex;

                       if(window.selectedIds.has(id)) addResizeHandle(el);
                       
                       // Update Cache
                       window.lastAppliedState[id] = { ...state };
                   }
                 }
               });
               window.previousModifiedIds = currentIds;
              }
              
              const observer = new MutationObserver(() => applyAllTransforms());
              observer.observe(document.body, { childList: true, subtree: true });
              
              function addResizeHandle(el) {
                  if (el.querySelector('.resize-handle')) return;
                  const handle = document.createElement('div');
                  handle.className = 'resize-handle';
                  el.appendChild(handle);
                  handle.contentEditable = "false";
              }
              function removeResizeHandle(el) { const handle = el.querySelector('.resize-handle'); if (handle) handle.remove(); }
              function clearSelection() { document.querySelectorAll('.vibe-selected').forEach(el => { el.classList.remove('vibe-selected'); removeResizeHandle(el); }); window.selectedIds.clear(); window.parent.postMessage({ type: 'SELECTION_CHANGE', id: null }, '*'); }
              
              function selectElement(el, id) {
                  if (!window.selectedIds.has(id)) {
                      clearSelection();
                      window.selectedIds.add(id);
                      el.classList.add('vibe-selected');
                      addResizeHandle(el);
                      window.parent.postMessage({ type: 'SELECTION_CHANGE', id }, '*');
                  }
              }
              
              document.addEventListener('contextmenu', (e) => {
                  if (!window.IS_EDIT_MODE) return;
                  e.preventDefault();
                  const target = e.target.closest('[data-vibe-id]');
                  if (target) {
                      if (!window.selectedIds.has(target.getAttribute('data-vibe-id'))) {
                          clearSelection();
                          selectElement(target, target.getAttribute('data-vibe-id'));
                      }
                      window.parent.postMessage({ type: 'CONTEXT_MENU', x: e.clientX, y: e.clientY }, '*');
                  } else {
                      window.parent.postMessage({ type: 'CONTEXT_MENU', x: e.clientX, y: e.clientY, isBackground: true }, '*');
                  }
              });

              document.addEventListener('dblclick', (e) => {
               if (!window.IS_EDIT_MODE) return;
               
               let editableTarget = e.target;
               if (!editableTarget.isContentEditable) {
                   editableTarget.contentEditable = "true";
               }

               e.preventDefault(); e.stopPropagation();
               isEditingText = true;
               
               const wrapper = editableTarget.closest('.vibe-selected');
               if (wrapper) removeResizeHandle(wrapper);
               
               editableTarget.focus();
               
               const finishEdit = () => {
                  editableTarget.contentEditable = "false";
                  isEditingText = false;
                  window.getSelection().removeAllRanges();
                  editableTarget.style.userSelect = "none";
                  
                  let root = editableTarget.closest('[data-vibe-id]');
                  if(!root) return;
                  
                  let updateId = root.getAttribute('data-vibe-id');
                  if (editableTarget !== root) {
                      const tagName = editableTarget.tagName;
                      const index = Array.from(root.getElementsByTagName(tagName)).indexOf(editableTarget);
                      updateId = \`\${updateId}::\${tagName}-\${index}\`;
                  }

                  if (window.selectedIds.has(updateId) || (wrapper && wrapper.getAttribute('data-vibe-id') === updateId)) {
                      addResizeHandle(editableTarget === root ? root : editableTarget);
                  }
                  
                  const text = editableTarget.innerText;
                  const state = getTransform(updateId);
                  state.text = text;
                  window.parent.postMessage({ type: 'LAYOUT_UPDATE', id: updateId, text }, '*');
                  
                  editableTarget.removeEventListener('blur', finishEdit);
               };
               editableTarget.addEventListener('blur', finishEdit);
               editableTarget.addEventListener('keydown', (ev) => { if (ev.key === 'Enter' && !ev.shiftKey) { ev.preventDefault(); editableTarget.blur(); } });
              });

              document.addEventListener('mousedown', (e) => {
                window.parent.postMessage({ type: 'CLOSE_CONTEXT_MENU' }, '*');
                
                if (!window.IS_EDIT_MODE || isEditingText) return;
                if (e.target.isContentEditable) return;
                if (e.detail > 1) return; 
                if (e.button === 2) return; 
                
                // Handle Resize
                if (e.target.classList.contains('resize-handle')) {
                   e.preventDefault(); e.stopPropagation();
                   isResizing = true;
                   activeElement = e.target.parentElement;
                   startX = e.clientX; startY = e.clientY;
                   startWidth = activeElement.offsetWidth; startHeight = activeElement.offsetHeight;
                   
                   // IMPORTANT: Break flex layout to allow manual resizing
                   activeElement.style.flex = "none"; 
                   return;
                }
                
                // --- NEW SMART SELECTION LOGIC ---
                let selectionTarget = e.target;
                
                // Find closest container that has an ID
                let root = selectionTarget.closest('[data-vibe-id]');
                
                if (!root) { 
                   clearSelection(); 
                   return; 
                }
                
                e.preventDefault(); e.stopPropagation();
                startX = e.clientX; startY = e.clientY;
                isDragging = true;
                
                const rootId = root.getAttribute('data-vibe-id');
                let targetId = rootId;
                let targetEl = root;

                // Smart Drill Down: If we clicked a child, check if we should select it directly
                // We select directly if it's a significant tag (button, img, text) OR if parent is already selected
                if (selectionTarget !== root) {
                    const tagName = selectionTarget.tagName;
                    // Tags that should be selectable immediately
                    const directSelectTags = ['BUTTON', 'INPUT', 'IMG', 'svg', 'H1', 'H2', 'H3', 'H4', 'H5', 'P', 'SPAN', 'A', 'LI', 'LABEL'];
                    
                    if (directSelectTags.includes(tagName) || (selectionTarget.innerText && selectionTarget.children.length === 0)) {
                         const index = Array.from(root.getElementsByTagName(tagName)).indexOf(selectionTarget);
                         if (index !== -1) {
                             targetId = \`\${rootId}::\${tagName}-\${index}\`;
                             targetEl = selectionTarget;
                         }
                    }
                }
                
                // Select the specific target immediately
                selectElement(targetEl, targetId);
                lockedRect = targetEl.getBoundingClientRect();
              });

              document.addEventListener('mousemove', (e) => {
                if (!window.IS_EDIT_MODE || isEditingText) return;
                if (isResizing && activeElement) {
                   const dx = (e.clientX - startX) / window.CURRENT_ZOOM;
                   const dy = (e.clientY - startY) / window.CURRENT_ZOOM;
                   const newWidth = Math.max(20, startWidth + dx);
                   const newHeight = Math.max(20, startHeight + dy);
                   activeElement.style.width = newWidth + 'px';
                   activeElement.style.height = newHeight + 'px';
                   return;
                }
                if (isDragging) {
                   const dx = e.clientX - startX; const dy = e.clientY - startY;
                   window.selectedIds.forEach(id => {
                      let el = getElementByIdOrPath(id);
                      if(el) updateTransformWithId(el, id, dx, dy); 
                   });
                }
              });

              document.addEventListener('mouseup', (e) => {
                lockedRect = null; 
                if (isResizing && activeElement) {
                   const id = window.selectedIds.values().next().value; 
                   if (id) {
                       const width = parseInt(activeElement.style.width);
                       const height = parseInt(activeElement.style.height);
                       const state = getTransform(id);
                       state.width = width; state.height = height;
                       window.parent.postMessage({ type: 'LAYOUT_UPDATE', id, width, height }, '*');
                   }
                   isResizing = false; activeElement = null;
                } else if (isDragging) {
                   const dx = e.clientX - startX; const dy = e.clientY - startY;
                   if (Math.abs(dx) > 2 || Math.abs(dy) > 2) {
                       window.selectedIds.forEach(id => {
                          const el = getElementByIdOrPath(id);
                          if(el) updateTransformWithId(el, id, dx, dy, true);
                       });
                   }
                }
                isDragging = false;
              });
              
              function updateTransformWithId(el, id, deltaX, deltaY, isFinal = false) {
                 const state = getTransform(id);
                 const adjustedDx = deltaX / window.CURRENT_ZOOM;
                 const adjustedDy = deltaY / window.CURRENT_ZOOM;
                 const currentX = (state.dx || 0) + adjustedDx;
                 const currentY = (state.dy || 0) + adjustedDy;
                 el.style.transform = \`translate(\${currentX}px, \${currentY}px)\`;
                 if (isFinal) {
                    state.dx = currentX; state.dy = currentY;
                    window.parent.postMessage({ type: 'LAYOUT_UPDATE', id, dx: currentX, dy: currentY }, '*');
                 }
              }
          </script>
          <script type="text/babel">${sanitizedCode}
            const root = ReactDOM.createRoot(document.getElementById('root'));
            try {
              if (typeof GeneratedPage !== 'undefined') root.render(<GeneratedPage />);
              else root.render(<div className="p-4 text-red-500 font-bold border border-red-200 bg-red-50 rounded">Error: Component not found. The code may be invalid.</div>);
            } catch (err) {
              root.render(<div className="p-4 text-red-500 font-bold border border-red-200 bg-red-50 rounded">{err.toString()}</div>);
            }
          </script>
        </body>
      </html>
    `;
    iframeRef.current.srcdoc = html;
  }, [code, isDarkMode]); 

  if (!code) return <div className="h-full flex flex-col items-center justify-center text-gray-400">Generate code first</div>;

  return (
    <div 
        className={`w-full h-full p-4 relative overflow-auto flex items-center justify-center ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'}`}
        // FIX: Changed setContextMenu to onContextMenu (prop)
        onClick={() => onContextMenu && onContextMenu(null)} 
    >
       <div 
         onClick={(e) => e.stopPropagation()}
         style={{ width: '100%', height: '100%', transform: `scale(${zoom})`, transformOrigin: 'top center', transition: 'transform 0.2s ease-out' }} 
         className={`rounded-lg shadow-lg overflow-hidden border transition-colors duration-300 ${isEditMode ? 'border-indigo-400 ring-2 ring-indigo-100' : 'border-gray-200'} ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}
       >
         <iframe ref={iframeRef} title="Live Preview" className="w-full h-full border-none" sandbox="allow-scripts allow-same-origin" />
       </div>
    </div>
  );
};

// --- Whiteboard Components ---

const DraggableItem = ({ id, label, icon, onDragStart }) => (
  <div draggable onDragStart={(e) => onDragStart(e, 'sidebar', id)} className="flex items-center gap-3 p-3 mb-2 bg-white border border-gray-200 rounded-lg cursor-grab hover:shadow-md hover:border-indigo-300 transition-all group active:cursor-grabbing select-none">
    <div className="text-gray-400 group-hover:text-indigo-600 transition-colors">{icon}</div>
    <span className="text-sm font-medium text-gray-700">{label}</span>
  </div>
);

const WhiteboardItem = ({ item, index, onResizeStart, isSpacePressed, isSelected }) => {
  const definition = UI_ELEMENTS.find(el => el.id === item.type);
  const Preview = MOCK_PREVIEWS[item.type];
  const width = item.width || definition?.width || 200;
  const height = item.height || definition?.height || 100;
  const zIndex = item.zIndex !== undefined ? item.zIndex : 10;

  return (
    <div 
      style={{ position: 'absolute', left: item.x, top: item.y, width, height, zIndex: zIndex }}
      className={`group absolute bg-white shadow-sm transition-shadow hover:shadow-md ${isSpacePressed ? 'pointer-events-none' : ''} ${isSelected ? 'ring-2 ring-indigo-500 z-20' : ''}`}
    >
      <div className="w-full h-full overflow-hidden relative">
         <div className="w-full h-full pointer-events-none">{Preview}</div>
         <div className="absolute top-0 left-0 bg-indigo-600 text-white text-[9px] px-1.5 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">{definition?.label}</div>
      </div>
      {!isSpacePressed && isSelected && (
        <div 
          className="absolute bottom-0 right-0 w-4 h-4 bg-white border-2 border-indigo-500 rounded-full cursor-se-resize z-30 opacity-0 group-hover:opacity-100 hover:scale-125 transition-all"
          onMouseDown={(e) => onResizeStart(e, index)}
        />
      )}
    </div>
  );
};

// --- Context Menu ---
const ContextMenu = ({ x, y, selectedCount, onAction, hasGroups }) => {
  return (
    <div 
      onClick={(e) => e.stopPropagation()}
      className="absolute bg-white border border-gray-200 rounded-lg shadow-xl py-1 z-50 min-w-[160px] animate-in fade-in zoom-in-95 duration-100"
      style={{ left: x, top: y }}
    >
      <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">{selectedCount} Selected</div>
      <div className="h-px bg-gray-100 my-1"></div>
      
      <button onClick={() => onAction('layer-front')} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
         <ArrowUpFromLine size={14}/> Bring to Front
      </button>
      <button onClick={() => onAction('layer-forward')} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
         <ArrowUp size={14}/> Bring Forward
      </button>
      <button onClick={() => onAction('layer-backward')} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
         <ArrowDown size={14}/> Send Backward
      </button>
      <button onClick={() => onAction('layer-back')} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
         <ArrowDownToLine size={14}/> Send to Back
      </button>
      
      <div className="h-px bg-gray-100 my-1"></div>
      
      <button onClick={() => onAction('copy')} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
         <Copy size={14}/> Copy
      </button>
      <button onClick={() => onAction('paste')} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
         <Clipboard size={14}/> Paste
      </button>
      
      <div className="h-px bg-gray-100 my-1"></div>
      
      <button onClick={() => onAction('group')} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
         <Group size={14}/> Group
      </button>
      {hasGroups && (
        <button onClick={() => onAction('ungroup')} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
            <Ungroup size={14}/> Ungroup
        </button>
      )}
      
      <div className="h-px bg-gray-100 my-1"></div>
      
      <button onClick={() => onAction('delete')} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">
         <Trash2 size={14}/> Delete
      </button>
    </div>
  );
};


// --- Main App ---

export default function App() {
  const [canvasItems, setCanvasItems] = useState([]);
  const [generatedCode, setGeneratedCode] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState('canvas');
  const [isPreviewEditMode, setIsPreviewEditMode] = useState(false);
  const [layoutModifications, setLayoutModifications] = useState({});
  const [history, setHistory] = useState([]); 
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [isSpacePressed, setIsSpacePressed] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [selectedElementId, setSelectedElementId] = useState(null); 
  const [color, setColor] = useState("#6366f1");
  const [fontSize, setFontSize] = useState("16");
  const [clipboard, setClipboard] = useState([]); 
  const [isCopied, setIsCopied] = useState(false);
  const [websiteContext, setWebsiteContext] = useState("");
  const [aiPrompt, setAiPrompt] = useState("");
  const [isDarkMode, setIsDarkMode] = useState(false);
    
  // Whiteboard State
  const [wbHistory, setWbHistory] = useState([]); // Whiteboard Undo History
  const [resizingState, setResizingState] = useState(null);
  const [whiteboardSelection, setWhiteboardSelection] = useState(new Set());
  const [isMarquee, setIsMarquee] = useState(false);
  const [marqueeStart, setMarqueeStart] = useState({ x: 0, y: 0 });
  const [marqueeCurrent, setMarqueeCurrent] = useState({ x: 0, y: 0 });
  const [dragState, setDragState] = useState(null);
  const dragStartSnapshot = useRef(null);
    
  // Context Menu State
  const [contextMenu, setContextMenu] = useState(null); 

  const canvasRef = useRef(null); 
  const viewportRef = useRef(null); 

  // --- AUTOMATIC TAILWIND INJECTION FOR LOCALHOST ---
  useEffect(() => {
    // Inject Tailwind CDN for the main application UI if not present
    if (!document.querySelector('script[src*="tailwindcss"]')) {
      const script = document.createElement('script');
      script.src = "https://cdn.tailwindcss.com";
      document.head.appendChild(script);
    }
  }, []);
    
  // Helper to save history state
  // FIX: Wrapped in useCallback
  const saveToHistory = useCallback(() => {
    // Deep copy array to ensure no reference mutations in history
    const itemsToSave = [...(canvasItems || [])];
    setWbHistory(prev => [...prev.slice(-19), itemsToSave]);
  }, [canvasItems]);
    
  // FIX: Wrapped in useCallback
  const handleWhiteboardUndo = useCallback(() => {
    if (wbHistory.length === 0) return;
    const previous = wbHistory[wbHistory.length - 1];
    // Robust check - explicit undefined check
    if (previous !== undefined) { 
        setWbHistory(prev => prev.slice(0, -1));
        setCanvasItems(previous);
    }
  }, [wbHistory]);

  const handlePreviewLayoutUpdate = useCallback((data) => {
    const { id, dx, dy, width, height, text, color, fontSize, zIndex } = data; 
    
    // FIX: CREATE A DEEP COPY OF THE CURRENT STATE FOR HISTORY
    const currentStateSnapshot = JSON.parse(JSON.stringify(layoutModifications));
    setHistory(prev => [...prev, currentStateSnapshot]);

    setLayoutModifications(prev => ({
       ...prev,
       [id]: { ...(prev[id]||{}), ...(dx!==undefined&&{dx}), ...(dy!==undefined&&{dy}), ...(width!==undefined&&{width}), ...(height!==undefined&&{height}), ...(text!==undefined&&{text}), ...(color!==undefined&&{color}), ...(fontSize!==undefined&&{fontSize}), ...(zIndex!==undefined&&{zIndex}) }
    }));
    setUnsavedChanges(true);
  }, [layoutModifications]);
    
  // FIX: Wrapped in useCallback
  const handleUndo = useCallback(() => {
    if (history.length === 0) return;
    const previousState = history[history.length - 1];
    setHistory(prev => prev.slice(0, -1));
    setLayoutModifications(previousState);
    if (history.length === 1 && Object.keys(previousState).length === 0) setUnsavedChanges(false);
  }, [history]);

  // Helper to check if any selected item is part of a group
  // FIX: Wrapped in useCallback
  const isAnyGroupSelected = useCallback(() => {
      // Robust check
      const currentItems = canvasItems || [];
      for (let id of whiteboardSelection) {
          const item = currentItems.find(i => i.uniqueId === id);
          if (item && item.groupId) return true;
      }
      return false;
  }, [canvasItems, whiteboardSelection]);

  // FIX: Wrapped in useCallback and added default case
  const performAction = useCallback((action) => {
     setContextMenu(null); 
     
     const selectedIds = Array.from(whiteboardSelection);
     // Robust check for canvasItems
     const currentItems = canvasItems || [];
     
     if (activeTab === 'canvas' && action !== 'paste' && selectedIds.length === 0) return;
     
     // Save history before mutation
     if (['delete', 'paste', 'layer-up', 'layer-down', 'layer-front', 'layer-back', 'layer-forward', 'layer-backward', 'group', 'ungroup'].includes(action)) {
         saveToHistory();
     }
     
     switch(action) {
         case 'delete':
             setCanvasItems(prev => (prev || []).filter(i => !whiteboardSelection.has(i.uniqueId)));
             setWhiteboardSelection(new Set());
             break;
         case 'copy':
             if (selectedIds.length > 0) {
                 const selected = currentItems.filter(i => whiteboardSelection.has(i.uniqueId));
                 setClipboard(selected);
             }
             break;
         case 'paste':
             if (clipboard.length > 0) {
                 const groupIdMap = {};
                 const newItems = clipboard.map(item => {
                     let newGroupId = item.groupId;
                     if (item.groupId) {
                         if (!groupIdMap[item.groupId]) {
                             groupIdMap[item.groupId] = `group-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
                         }
                         newGroupId = groupIdMap[item.groupId];
                     }
                     return {
                         ...item,
                         x: item.x + 20, y: item.y + 20,
                         id: Date.now() + Math.random(),
                         uniqueId: `${item.type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                         zIndex: (item.zIndex || 10) + 1,
                         groupId: newGroupId
                     };
                 });
                 setCanvasItems(prev => [...(prev || []), ...newItems]);
                 setWhiteboardSelection(new Set(newItems.map(i => i.uniqueId)));
             }
             break;
         case 'layer-front': // Send to Front
             const maxZ = Math.max(...currentItems.map(i => i.zIndex || 10), 10);
             setCanvasItems(prev => (prev || []).map(item => {
                 if (whiteboardSelection.has(item.uniqueId)) {
                     return { ...item, zIndex: maxZ + 1 };
                 }
                 return item;
             }));
             if (activeTab === 'preview') {
                 selectedIds.forEach(id => {
                     handlePreviewLayoutUpdate({ id, zIndex: maxZ + 1 });
                 });
             }
             break;
         case 'layer-back': // Send to Back
             const minZ = Math.min(...currentItems.map(i => i.zIndex || 10), 10);
             setCanvasItems(prev => (prev || []).map(item => {
                 if (whiteboardSelection.has(item.uniqueId)) {
                     return { ...item, zIndex: minZ - 1 };
                 }
                 return item;
             }));
             if (activeTab === 'preview') {
                 selectedIds.forEach(id => {
                     handlePreviewLayoutUpdate({ id, zIndex: minZ - 1 });
                 });
             }
             break;
         case 'layer-forward': // Bring Forward
         case 'layer-up':
             // Find nearest Z-index above current selection's max
             const currentZValues = currentItems.filter(i => whiteboardSelection.has(i.uniqueId)).map(i => i.zIndex || 10);
             const currentMax = Math.max(...currentZValues);
             const itemsAbove = currentItems.filter(i => (i.zIndex || 10) > currentMax && !whiteboardSelection.has(i.uniqueId));
             const nextZ = itemsAbove.length > 0 ? Math.min(...itemsAbove.map(i => i.zIndex || 10)) : currentMax;
             const newForwardZ = nextZ + 1;

             setCanvasItems(prev => (prev || []).map(item => {
                 if (whiteboardSelection.has(item.uniqueId)) {
                     return { ...item, zIndex: newForwardZ };
                 }
                 return item;
             }));
             if (activeTab === 'preview') {
                 selectedIds.forEach(id => {
                     handlePreviewLayoutUpdate({ id, zIndex: newForwardZ });
                 });
             }
             break;
         case 'layer-backward': // Send Backward
         case 'layer-down':
             // Find nearest Z-index below current selection's min
             const currentZValuesDown = currentItems.filter(i => whiteboardSelection.has(i.uniqueId)).map(i => i.zIndex || 10);
             const currentMin = Math.min(...currentZValuesDown);
             const itemsBelow = currentItems.filter(i => (i.zIndex || 10) < currentMin && !whiteboardSelection.has(i.uniqueId));
             const prevZ = itemsBelow.length > 0 ? Math.max(...itemsBelow.map(i => i.zIndex || 10)) : currentMin;
             const newBackwardZ = prevZ - 1;

             setCanvasItems(prev => (prev || []).map(item => {
                 if (whiteboardSelection.has(item.uniqueId)) {
                     return { ...item, zIndex: newBackwardZ };
                 }
                 return item;
             }));
             if (activeTab === 'preview') {
                 selectedIds.forEach(id => {
                     handlePreviewLayoutUpdate({ id, zIndex: newBackwardZ });
                 });
             }
             break;
         case 'group':
             const groupId = `group-${Date.now()}`;
             setCanvasItems(prev => (prev || []).map(item => {
                 if (whiteboardSelection.has(item.uniqueId)) {
                     return { ...item, groupId };
                 }
                 return item;
             }));
             break;
         case 'ungroup':
             setCanvasItems(prev => (prev || []).map(item => {
                if (whiteboardSelection.has(item.uniqueId)) {
                    const { groupId, ...rest } = item;
                    return rest;
                }
                return item;
             }));
             break;
         default:
             break;
     }
  }, [canvasItems, whiteboardSelection, activeTab, clipboard, handlePreviewLayoutUpdate, saveToHistory]);

  // FIX: Added missing dependencies to useEffect
  useEffect(() => {
    const handleKeyDown = (e) => { 
        if (e.code === 'Space' && !e.repeat && e.target.tagName !== 'INPUT') { e.preventDefault(); setIsSpacePressed(true); }
        if (e.key === 'Delete' || e.key === 'Backspace') performAction('delete');
        if ((e.ctrlKey || e.metaKey) && e.key === 'c') performAction('copy');
        if ((e.ctrlKey || e.metaKey) && e.key === 'v') performAction('paste');
        if ((e.ctrlKey || e.metaKey) && e.key === 'g') { e.preventDefault(); performAction('group'); }
        if ((e.ctrlKey || e.metaKey) && e.key === 'z') { 
            e.preventDefault(); 
            e.stopPropagation(); // Stop propagation to prevent browser undo
            if (activeTab === 'preview') handleUndo();
            else handleWhiteboardUndo(); 
        }
        if (e.key === 'Escape') { setContextMenu(null); setWhiteboardSelection(new Set()); }
    };
    const handleKeyUp = (e) => { if (e.code === 'Space') { e.preventDefault(); setIsSpacePressed(false); setIsPanning(false); } };
    const handleIframeMessage = (e) => { 
        if (e.data.type === 'SPACE_DOWN') setIsSpacePressed(true); 
        if (e.data.type === 'SPACE_UP') { setIsSpacePressed(false); setIsPanning(false); }
    };
    window.addEventListener('keydown', handleKeyDown, { passive: false });
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('message', handleIframeMessage);
    return () => { window.removeEventListener('keydown', handleKeyDown); window.removeEventListener('keyup', handleKeyUp); window.removeEventListener('message', handleIframeMessage); };
  }, [activeTab, whiteboardSelection, clipboard, canvasItems, wbHistory, history, performAction, handleUndo, handleWhiteboardUndo]);

  const handleContextMenu = (e, isFromIframe = false) => {
      e.preventDefault();
      let x = e.clientX;
      let y = e.clientY;
      setContextMenu({ x, y });
  };
    
  const handlePreviewContextMenu = (data) => {
      if (!data) {
        setContextMenu(null);
        return;
      }
      setContextMenu({ x: data.x, y: data.y });
  };

  // FIX: Removed unused handleMouseDown (used inline or via handleWhiteboardMouseDown)

  const handleWhiteboardMouseDown = (e) => {
    if (contextMenu) setContextMenu(null); // Click-to-close menu
    
    if (isSpacePressed) { setIsPanning(true); return; }
    if (resizingState) return;
    
    const currentItems = canvasItems || [];
    
    if (e.button === 2) {
          const rect = canvasRef.current.getBoundingClientRect();
          const mouseX = (e.clientX - rect.left) / zoom;
          const mouseY = (e.clientY - rect.top) / zoom;
          const clickedItem = currentItems.slice().sort((a,b) => (b.zIndex||10) - (a.zIndex||10)).find(item => {
             return mouseX >= item.x && mouseX <= item.x + item.width &&
                    mouseY >= item.y && mouseY <= item.y + item.height;
          });
        if (clickedItem && !whiteboardSelection.has(clickedItem.uniqueId)) {
             const groupIdsToSelect = clickedItem.groupId ? currentItems.filter(i => i.groupId === clickedItem.groupId).map(i => i.uniqueId) : [clickedItem.uniqueId];
             setWhiteboardSelection(new Set(groupIdsToSelect));
        }
        return; 
    }

    const rect = canvasRef.current.getBoundingClientRect();
    const mouseX = (e.clientX - rect.left) / zoom;
    const mouseY = (e.clientY - rect.top) / zoom;

    const clickedItem = currentItems.slice().sort((a,b) => (b.zIndex||10) - (a.zIndex||10)).find(item => {
        return mouseX >= item.x && mouseX <= item.x + item.width &&
               mouseY >= item.y && mouseY <= item.y + item.height;
    });

    if (clickedItem) {
        let newSelection = new Set(whiteboardSelection);
        const groupIdsToSelect = clickedItem.groupId ? currentItems.filter(i => i.groupId === clickedItem.groupId).map(i => i.uniqueId) : [clickedItem.uniqueId];
        
        if (e.shiftKey) {
            if (newSelection.has(clickedItem.uniqueId)) {
                groupIdsToSelect.forEach(id => newSelection.delete(id));
            } else {
                groupIdsToSelect.forEach(id => newSelection.add(id));
            }
        } else {
            if (!newSelection.has(clickedItem.uniqueId)) {
                newSelection = new Set(groupIdsToSelect);
            }
        }
        
        setWhiteboardSelection(newSelection);
        
        dragStartSnapshot.current = currentItems;

        const initialPositions = {};
        currentItems.forEach(item => {
            if (newSelection.has(item.uniqueId)) {
                initialPositions[item.uniqueId] = { x: item.x, y: item.y };
            }
        });

        setDragState({
            startX: e.clientX,
            startY: e.clientY,
            initialItems: initialPositions
        });
    } else {
        if (!e.shiftKey) setWhiteboardSelection(new Set());
        setContextMenu(null); 
        
        setIsMarquee(true);
        setMarqueeStart({ x: mouseX, y: mouseY });
        setMarqueeCurrent({ x: mouseX, y: mouseY });
    }
  };

  const handleWhiteboardMouseMove = (e) => {
    if (isPanning && viewportRef.current) {
        viewportRef.current.scrollLeft -= e.movementX;
        viewportRef.current.scrollTop -= e.movementY;
        return;
    }

    const rect = canvasRef.current.getBoundingClientRect();
    const mouseX = (e.clientX - rect.left) / zoom;
    const mouseY = (e.clientY - rect.top) / zoom;

    if (resizingState) {
        const dx = (e.clientX - resizingState.startX) / zoom;
        const dy = (e.clientY - resizingState.startY) / zoom;
        setCanvasItems(prev => {
            const newItems = [...(prev || [])];
            // FIX: Use immutable update pattern. Create a NEW object for the modified item.
            // Previous code was mutating the object in place, corrupting the history snapshots.
            const itemIndex = resizingState.index;
            if (newItems[itemIndex]) {
                 newItems[itemIndex] = {
                    ...newItems[itemIndex],
                    width: Math.max(50, resizingState.startWidth + dx),
                    height: Math.max(50, resizingState.startHeight + dy)
                 };
            }
            return newItems;
        });
        return;
    }

    if (dragState) {
        const dx = (e.clientX - dragState.startX) / zoom;
        const dy = (e.clientY - dragState.startY) / zoom;
        
        setCanvasItems(prev => (prev || []).map(item => {
            if (dragState.initialItems[item.uniqueId]) {
                const init = dragState.initialItems[item.uniqueId];
                return { ...item, x: init.x + dx, y: init.y + dy };
            }
            return item;
        }));
        return;
    }

    if (isMarquee) {
        setMarqueeCurrent({ x: mouseX, y: mouseY });
        const x1 = Math.min(marqueeStart.x, mouseX);
        const y1 = Math.min(marqueeStart.y, mouseY);
        const x2 = Math.max(marqueeStart.x, mouseX);
        const y2 = Math.max(marqueeStart.y, mouseY);

        const selected = new Set();
        const currentItems = canvasItems || [];
        currentItems.forEach(item => {
            if (item.x < x2 && item.x + item.width > x1 &&
                item.y < y2 && item.y + item.height > y1) {
                selected.add(item.uniqueId);
            }
        });
        setWhiteboardSelection(selected);
    }
  };

  const handleWhiteboardMouseUp = () => {
    if (dragState || resizingState) {
        // Only save if content changed to avoid empty history states
        // Use JSON.stringify for deep comparison to ensure actual changes occurred
        if (dragStartSnapshot.current && JSON.stringify(dragStartSnapshot.current) !== JSON.stringify(canvasItems)) {
             // Deep copy current snapshot to history
             const snapshotToSave = [...dragStartSnapshot.current];
             setWbHistory(prev => [...prev.slice(-19), snapshotToSave]);
        }
    }
    
    setIsPanning(false);
    setIsMarquee(false);
    setDragState(null);
    setResizingState(null);
    dragStartSnapshot.current = null;
  };

  const handleWhiteboardResizeStart = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    dragStartSnapshot.current = canvasItems; 
    
    const currentItems = canvasItems || [];
    const item = currentItems[index];
    setResizingState({
        index,
        startX: e.clientX,
        startY: e.clientY,
        startWidth: item.width || 200, 
        startHeight: item.height || 100
    });
  };

  const handleDragStart = (e, source, type) => {
    e.dataTransfer.setData("source", source);
    e.dataTransfer.setData("type", type);
  };

  // FIX: Removed unused handleDragOver

  const handleDrop = (e) => {
    e.preventDefault();
    const source = e.dataTransfer.getData("source");
    const type = e.dataTransfer.getData("type");
    if (source === 'sidebar' && type) {
       saveToHistory(); 
       const rect = canvasRef.current.getBoundingClientRect();
       const x = (e.clientX - rect.left) / zoom;
       const y = (e.clientY - rect.top) / zoom;
       const def = UI_ELEMENTS.find(el => el.id === type);
       
       const currentItems = canvasItems || [];
       const newItem = {
           type, x, y, width: def.width, height: def.height, id: Date.now() + Math.random(), uniqueId: `${type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
           zIndex: 10 + currentItems.length 
       };
       setCanvasItems([...currentItems, newItem]);
       setWhiteboardSelection(new Set([newItem.uniqueId]));
    }
  };

  const handleGenerate = async (forceRegenerate = false) => {
    if (!canvasItems || canvasItems.length === 0) return;
    setIsGenerating(true);
    setActiveTab('preview');
    
    // Strictly determine if this is a save/refinement operation
    // It is a refinement ONLY IF: we are NOT forcing regeneration, we HAVE generated code, and we HAVE modifications.
    const isRefactoring = !forceRegenerate && !!generatedCode && unsavedChanges && Object.keys(layoutModifications).length > 0;
    
    const code = await generateCodeWithGemini(
        canvasItems, 
        isRefactoring ? generatedCode : null, // If regenerating, pass null to force fresh generation
        isRefactoring ? layoutModifications : null, // If regenerating, ignore mods
        websiteContext, 
        null
    );
    
    setGeneratedCode(code);
    setIsGenerating(false);
    setUnsavedChanges(false);
    setLayoutModifications({});
    setHistory([]);
    setIsPreviewEditMode(false); 
  };

  const handleAiRefine = async () => {
    if (!generatedCode) return;
    setIsGenerating(true);
    // Pass existing code, mods, and the specific prompt
    const code = await generateCodeWithGemini(canvasItems, generatedCode, layoutModifications, websiteContext, aiPrompt);
    setGeneratedCode(code);
    setIsGenerating(false);
    setUnsavedChanges(false);
    setLayoutModifications({});
    setHistory([]);
    setAiPrompt(""); // Clear prompt after use
    setIsPreviewEditMode(false);
  };

  const handleResetModifications = () => {
    setLayoutModifications({});
    setHistory([]);
    setUnsavedChanges(false);
  };

  // FIX: Removed unused removeItem function

  const copyToClipboard = () => { 
      // Use document.execCommand('copy') as a robust fallback for restricted environments
      const textArea = document.createElement("textarea");
      textArea.value = generatedCode;
      
      // Ensure it's not visible but part of DOM
      textArea.style.position = "fixed";
      textArea.style.left = "-9999px";
      textArea.style.top = "0";
      document.body.appendChild(textArea);
      
      textArea.focus();
      textArea.select();
      
      try {
        const successful = document.execCommand('copy');
        if(successful) {
           setIsCopied(true);
           setTimeout(() => setIsCopied(false), 2000);
        } else {
           console.error("Copy failed.");
        }
      } catch (err) {
        console.error("Copy error:", err);
      }
      
      document.body.removeChild(textArea);
  };
  const handleZoomIn = () => setZoom(z => Math.min(z + 0.1, 2));
  const handleZoomOut = () => setZoom(z => Math.max(z - 0.1, 0.2));
  
  // FIX: Removed unused handleMouseDown (used inline now for space key check if needed, or via handleWhiteboardMouseDown)
  
  const handleMouseMove = (e) => { 
      if (isPanning && viewportRef.current) { 
          viewportRef.current.scrollLeft -= e.movementX; 
          viewportRef.current.scrollTop -= e.movementY; 
          return;
      }
      handleWhiteboardMouseMove(e);
  };
  const handleMouseUp = () => { 
      if(isPanning) setIsPanning(false);
      handleWhiteboardMouseUp();
  };

  return (
    <div 
       className={`flex h-screen w-full font-sans overflow-hidden transition-colors duration-300 ${isDarkMode ? 'bg-gray-950 text-gray-100' : 'bg-gray-50 text-gray-900'}`} 
       onClick={() => setContextMenu(null)}
       onContextMenu={(e) => e.preventDefault()}
    >
      
      {/* Context Menu */}
      {contextMenu && (
          <ContextMenu 
            x={contextMenu.x} 
            y={contextMenu.y} 
            selectedCount={whiteboardSelection.size} 
            onAction={performAction} 
            hasGroups={isAnyGroupSelected()}
          />
      )}

      <div className={`w-64 border-r flex flex-col shadow-xl z-20 shrink-0 transition-colors duration-300 ${isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
        <div className={`p-5 border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-100'}`}>
          <div className="flex items-center gap-2 text-indigo-600 mb-1"><Zap className="fill-current" size={20} /><h1 className="font-bold text-lg tracking-tight">Velvisio</h1></div>
          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Drag items to the whiteboard.</p>
        </div>
        <div className={`flex-1 overflow-y-auto p-4 space-y-1 ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50/50'}`}>
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-1">Elements</div>
          {UI_ELEMENTS.map((item) => <DraggableItem key={item.id} {...item} onDragStart={handleDragStart} />)}
        </div>
        <div className={`p-4 border-t ${isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}>
           <div className={`flex items-center gap-2 text-xs justify-center transition-colors ${isSpacePressed ? 'text-indigo-600 font-bold' : 'text-gray-400'}`}>
             {isSpacePressed ? <Hand size={12} /> : <Grid size={12} />}<span>{isSpacePressed ? "Pan Mode Active" : "Hold Space to Pan"}</span>
           </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col relative h-full min-w-0">
        
        <div className={`h-14 border-b flex justify-between items-center px-4 shadow-sm z-30 shrink-0 transition-colors duration-300 ${isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
          <div className={`flex p-1 rounded-lg shrink-0 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
             <button onClick={() => setActiveTab('canvas')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-2 ${activeTab === 'canvas' ? (isDarkMode ? 'bg-gray-700 text-indigo-400 shadow-sm' : 'bg-white text-indigo-600 shadow-sm') : 'text-gray-500 hover:text-gray-700'}`}><Layout size={14}/>Board</button>
             <button onClick={() => setActiveTab('preview')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-2 ${activeTab === 'preview' ? (isDarkMode ? 'bg-gray-700 text-indigo-400 shadow-sm' : 'bg-white text-indigo-600 shadow-sm') : 'text-gray-500 hover:text-gray-700'}`}><Eye size={14}/>Preview</button>
             <button onClick={() => setActiveTab('code')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-2 ${activeTab === 'code' ? (isDarkMode ? 'bg-gray-700 text-indigo-400 shadow-sm' : 'bg-white text-indigo-600 shadow-sm') : 'text-gray-500 hover:text-gray-700'}`}><Code size={14}/>Code</button>
          </div>

          <div className="flex items-center gap-1 mx-2">
             <button onClick={handleZoomOut} className="p-1.5 hover:bg-gray-100 rounded text-gray-500"><ZoomOut size={14} /></button>
             <span className="text-[10px] font-mono text-gray-400 w-8 text-center">{Math.round(zoom * 100)}%</span>
             <button onClick={handleZoomIn} className="p-1.5 hover:bg-gray-100 rounded text-gray-500"><ZoomIn size={14} /></button>
          </div>

          <div className="flex items-center gap-2 shrink-0 flex-1 min-w-0 justify-end">
             <button onClick={() => setIsDarkMode(!isDarkMode)} className={`p-2 rounded-full transition-colors shrink-0 ${isDarkMode ? 'bg-gray-800 text-yellow-400 hover:bg-gray-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} title={isDarkMode ? "Light Mode" : "Dark Mode"}>
                {isDarkMode ? <Sun size={14} /> : <Moon size={14} />}
             </button>

             {activeTab === 'canvas' && (
               <>
                 <div className={`flex items-center gap-2 rounded-md px-2 py-1 mr-2 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                    <MessageSquare size={14} className="text-gray-400" />
                    <input 
                      type="text" 
                      placeholder="e.g. Pizza Shop" 
                      value={websiteContext}
                      onChange={(e) => setWebsiteContext(e.target.value)}
                      className={`bg-transparent border-none focus:ring-0 text-xs w-32 p-0 ${isDarkMode ? 'text-gray-200 placeholder-gray-500' : 'text-gray-700 placeholder-gray-400'}`}
                    />
                 </div>
                 {wbHistory.length > 0 && <button onClick={handleWhiteboardUndo} className="p-1.5 hover:bg-gray-100 rounded text-gray-500" title="Undo Whiteboard"><Undo size={14} /></button>}
               </>
             )}
             
             {activeTab === 'preview' && (
                <div className={`flex items-center gap-2 mr-2 border-r pr-2 overflow-x-auto max-w-[40vw] scrollbar-hide ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                   {/* AI Refine Bar */}
                   {generatedCode && !isGenerating && (
                      <div className={`flex items-center gap-1 border rounded-md px-2 py-1 mr-2 shrink-0 ${isDarkMode ? 'bg-indigo-900/30 border-indigo-800' : 'bg-indigo-50 border-indigo-100'}`}>
                          <Wand2 size={12} className="text-indigo-500 shrink-0" />
                          <input 
                             type="text"
                             placeholder="Refine..."
                             value={aiPrompt}
                             onChange={(e) => setAiPrompt(e.target.value)}
                             onKeyDown={(e) => e.key === 'Enter' && handleAiRefine()}
                             className={`bg-transparent border-none focus:ring-0 text-xs w-24 sm:w-32 p-0 ${isDarkMode ? 'text-indigo-200 placeholder-indigo-700' : 'text-indigo-900 placeholder-indigo-300'}`}
                          />
                          <button onClick={handleAiRefine} disabled={!aiPrompt.trim()} className="text-[10px] font-bold text-indigo-600 hover:text-indigo-800 uppercase disabled:opacity-50 shrink-0">Go</button>
                      </div>
                   )}

                   {isPreviewEditMode && selectedElementId && (
                      <div className="flex items-center gap-2 shrink-0">
                        <div className={`flex items-center gap-1 p-1 rounded border shrink-0 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`} title="Font Size">
                           <TypeIcon size={14} className="text-gray-500 ml-1" />
                           <input type="number" value={fontSize} onChange={(e) => setFontSize(e.target.value)} className={`w-8 text-xs bg-transparent border-none focus:ring-0 text-right outline-none ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`} />
                           <span className="text-[10px] text-gray-400 mr-1">px</span>
                        </div>
                        <div className={`flex items-center gap-1 p-1 rounded border shrink-0 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`} title="Change Color">
                           <div className="w-4 h-4 rounded-full border border-gray-300" style={{ backgroundColor: color }}></div>
                           <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="opacity-0 absolute w-6 h-6 cursor-pointer" />
                        </div>
                      </div>
                   )}
                   {unsavedChanges && ( <button onClick={handleUndo} className="p-1.5 hover:bg-gray-100 rounded text-gray-500 shrink-0" title="Undo"><RotateCcw size={14} /></button> )}
                   {generatedCode && (
                     <button onClick={() => setIsPreviewEditMode(!isPreviewEditMode)} className={`p-1.5 rounded transition-colors shrink-0 ${isPreviewEditMode ? 'bg-indigo-100 text-indigo-600' : 'hover:bg-gray-100 text-gray-500'}`} title={isPreviewEditMode ? "Finish Editing" : "Enter Edit Mode"}><MousePointer2 size={16} /></button>
                   )}
                </div>
             )}

             {unsavedChanges ? (
                <div className="flex gap-1 shrink-0 ml-auto">
                    <button onClick={handleResetModifications} className={`px-3 py-1.5 rounded-lg font-medium text-xs transition-colors ${isDarkMode ? 'text-gray-400 bg-gray-800 hover:bg-gray-700' : 'text-gray-600 bg-gray-100 hover:bg-gray-200'}`}>Reset</button>
                    <button onClick={() => handleGenerate(false)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-medium text-xs text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm transition-all"><Save size={14} /> Save</button>
                </div>
             ) : (
                <button onClick={() => handleGenerate(false)} disabled={isGenerating || (!canvasItems || canvasItems.length === 0)} className={`flex items-center gap-2 px-4 py-1.5 rounded-lg font-medium text-xs text-white shadow-sm transition-all shrink-0 ml-auto ${isGenerating || (!canvasItems || canvasItems.length === 0) ? 'bg-gray-300 cursor-not-allowed' : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:shadow-md'}`}>
                    {isGenerating ? <RefreshCw size={14} className="animate-spin" /> : <Terminal size={14} />}
                    <span>{isGenerating ? 'Building...' : 'Generate'}</span>
                </button>
             )}
          </div>
        </div>

        <div className={`flex-1 overflow-hidden relative ${isDarkMode ? 'bg-gray-950' : 'bg-slate-100'}`}>
          {/* WHITEBOARD VIEW */}
          <div className={`absolute inset-0 transition-opacity duration-300 ${activeTab === 'canvas' ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'}`}>
            <div 
               ref={viewportRef} 
               className={`w-full h-full overflow-auto ${isSpacePressed ? 'cursor-grab' : 'cursor-default'}`} 
               onMouseDown={handleWhiteboardMouseDown}
               onMouseMove={handleMouseMove}
               onMouseUp={handleMouseUp}
               onDragOver={(e) => e.preventDefault()}
               onDrop={handleDrop}
               onContextMenu={handleContextMenu}
               style={{ backgroundImage: `radial-gradient(${isDarkMode ? '#374151' : '#cbd5e1'} 1px, transparent 1px)`, backgroundSize: '20px 20px' }}
            >
              <div ref={canvasRef} className="min-w-[2000px] min-h-[2000px] relative origin-top-left" style={{ transform: `scale(${zoom})` }}>
                {(!canvasItems || canvasItems.length === 0) && <div className="absolute inset-0 flex items-center justify-center pointer-events-none"><div className="text-center opacity-50" style={{ transform: `scale(${1/zoom})` }}><Move size={48} className={`mx-auto mb-4 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-300'}`} /><h3 className={`text-xl font-bold ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Infinite Canvas</h3><p className={isDarkMode ? 'text-gray-600' : 'text-gray-400'}>Click & Drag to Select. Space to Pan.</p></div></div>}
                
                {(canvasItems || []).map((item, index) => (
                    <WhiteboardItem 
                        key={item.id} 
                        item={item} 
                        index={index} 
                        isSelected={whiteboardSelection.has(item.uniqueId)}
                        onResizeStart={handleWhiteboardResizeStart}
                        isSpacePressed={isSpacePressed} 
                    />
                ))}

                {isMarquee && (
                    <div style={{
                        position: 'absolute',
                        border: '1px solid #6366f1',
                        backgroundColor: 'rgba(99, 102, 241, 0.1)',
                        left: Math.min(marqueeStart.x, marqueeCurrent.x),
                        top: Math.min(marqueeStart.y, marqueeCurrent.y),
                        width: Math.abs(marqueeCurrent.x - marqueeStart.x),
                        height: Math.abs(marqueeCurrent.y - marqueeStart.y),
                        pointerEvents: 'none',
                        zIndex: 1000
                    }} />
                )}
              </div>
            </div>
          </div>

          <div className={`absolute inset-0 bg-white overflow-hidden flex flex-col transition-opacity duration-300 ${activeTab === 'preview' ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'} ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
              {isGenerating ? (
               <div className="flex-1 flex flex-col items-center justify-center text-gray-500"><RefreshCw className="animate-spin mb-4 text-indigo-600" size={40} /><p className="text-lg font-medium">Gemini is {unsavedChanges ? 'saving your edits...' : 'architecting your site...'}</p></div>
              ) : (
               <LivePreview 
                 code={generatedCode} 
                 isEditMode={isPreviewEditMode} 
                 onLayoutUpdate={handlePreviewLayoutUpdate} 
                 zoom={zoom} 
                 initialModifications={layoutModifications}
                 selectedColor={color}
                 selectedFontSize={fontSize}
                 onSelectionChange={setSelectedElementId}
                 onContextMenu={handlePreviewContextMenu}
                 isDarkMode={isDarkMode}
               />
              )}
          </div>

          <div className={`absolute inset-0 bg-gray-900 text-gray-100 overflow-hidden flex flex-col transition-opacity duration-300 ${activeTab === 'code' ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'}`}>
            <div className="flex items-center justify-between p-4 border-b border-gray-800 bg-gray-900/95 backdrop-blur shrink-0">
              <span className="ml-3 text-sm text-gray-500 font-mono">GeneratedPage.jsx</span>
              <button onClick={copyToClipboard} className={`flex items-center gap-2 text-xs font-medium transition-all px-3 py-1.5 rounded ${isCopied ? 'bg-green-500/20 text-green-400' : 'text-gray-400 hover:text-white hover:bg-gray-800'}`}>
                  {isCopied ? <Check size={14} /> : <Copy size={14} />} 
                  {isCopied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <div className="flex-1 overflow-auto p-6 font-mono text-sm leading-relaxed">{generatedCode ? <pre className="whitespace-pre-wrap">{generatedCode}</pre> : <div className="h-full flex flex-col items-center justify-center text-gray-600"><Play size={48} className="mb-4 opacity-20" /><p>Generate code to view source</p></div>}</div>
          </div>
        </div>
      </div>
    </div>
  );
}